import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Star, Eye, Pencil, FileStack, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Modal, Button, Tooltip, useConfirm } from './ui';
import { formatDateTimeShort, getEstadoColor } from '../utils/helpers';

const ESTADO_LABELS = {
    borrador: 'Borrador',
    enviada: 'Enviada',
    aprobada: 'Aprobada',
    rechazada: 'Rechazada'
};

const formatCurrency = (n, moneda = 'PEN') => {
    const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
    return symbol + (parseFloat(n) || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

/**
 * Modal del hilo de versiones de una cotizacion.
 * Lista la padre + todas las versiones con su estado, autor, fecha y total.
 * Permite marcar otra version como "principal" (la visible en listado/kanban).
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   cotizacionId: id de cualquier coti del hilo (raiz o version)
 *   onChange: callback cuando cambia la principal (para refrescar el listado padre)
 */
const HiloVersionesModal = ({ open, onClose, cotizacionId, onChange }) => {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [marcando, setMarcando] = useState(null);

    useEffect(() => {
        if (!open || !cotizacionId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await api.cotizaciones.getHilo(cotizacionId);
                if (cancelled) return;
                setItems(res.data?.items || []);
            } catch (err) {
                if (cancelled) return;
                toast.error('No pudimos cargar el hilo de versiones');
                onClose?.();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, cotizacionId, onClose]);

    // Regla: si la principal actual del hilo esta aprobada o rechazada, el
    // hilo esta "cerrado" — ya no se puede cambiar la principal.
    const principalActual = items.find(i => i.es_version_principal);
    const hiloCerrado = principalActual && (
        principalActual.estado === 'aprobada' || principalActual.estado === 'rechazada'
    );

    const handleMarcarPrincipal = async (item) => {
        // Guard: si el hilo esta cerrado (principal aprobada/rechazada), no
        // permitir el cambio. Mensaje claro al user.
        if (hiloCerrado) {
            toast.error(
                `No se puede cambiar la versión principal porque la cotización ya fue ${principalActual.estado}.`
            );
            return;
        }
        const ok = await confirm({
            title: '¿Marcar como versión principal?',
            message: (
                <div>
                    <p>
                        <strong>{item.numero}</strong> reemplazará a{' '}
                        <strong>{principalActual?.numero || 'la versión actual'}</strong>{' '}
                        como la visible en el listado.
                    </p>
                    <p style={{ marginTop: 8, fontSize: 13, color: 'rgb(var(--muted))' }}>
                        Las demás versiones siguen accesibles desde este modal.
                    </p>
                </div>
            ),
            confirmText: 'Marcar como principal'
        });
        if (!ok) return;

        setMarcando(item.id);
        try {
            await api.cotizaciones.marcarPrincipal(item.id);
            toast.success(`${item.numero} es ahora la versión principal`);
            const res = await api.cotizaciones.getHilo(cotizacionId);
            setItems(res.data?.items || []);
            onChange?.();
        } catch (err) {
            toast.error('Error: ' + (err.message || 'No se pudo cambiar la principal'));
        } finally {
            setMarcando(null);
        }
    };

    const navegarA = (item, ruta) => {
        const path = ruta === 'detalle'
            ? `/cotizaciones/${item.id}/detalle`
            : `/cotizaciones/${item.id}`;
        onClose?.();
        navigate(path);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Versiones de esta cotización"
            size="lg"
        >
            {loading ? (
                <p className="text-sm text-muted">Cargando hilo…</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-muted">No hay versiones para mostrar.</p>
            ) : (
                <div className="hilo-versiones">
                    <p className="hilo-intro">
                        <FileStack size={14} aria-hidden="true" />
                        {items.length} {items.length === 1 ? 'cotización' : 'cotizaciones'} en este hilo.
                        Solo la marcada como <strong>principal</strong> aparece en el listado.
                    </p>
                    {hiloCerrado && (
                        <div className="hilo-aviso-cerrado" role="alert">
                            <div className="hilo-aviso-icon">
                                <Lock size={16} aria-hidden="true" />
                            </div>
                            <div className="hilo-aviso-body">
                                <div className="hilo-aviso-titulo">Cambio bloqueado</div>
                                <div className="hilo-aviso-msg">
                                    La versión principal ya fue{' '}
                                    <strong>{principalActual.estado === 'aprobada' ? 'aprobada' : 'rechazada'}</strong>.
                                    Ya no se pueden marcar otras versiones como principal.
                                </div>
                            </div>
                        </div>
                    )}
                    <ol className="hilo-timeline">
                        {items.map((item, idx) => {
                            const esRaiz = !item.cotizacion_padre_id;
                            const esPrincipal = !!item.es_version_principal;
                            const esBorrador = item.estado === 'borrador';
                            return (
                                <li
                                    key={item.id}
                                    className={`hilo-item ${esPrincipal ? 'hilo-item-principal' : ''}`}
                                >
                                    <div className="hilo-body">
                                        <div className="hilo-row-top">
                                            <span className="hilo-numero">
                                                {item.numero}
                                                {esRaiz && <span className="hilo-pill hilo-pill-raiz">RAÍZ</span>}
                                                {esPrincipal && (
                                                    <span className="hilo-pill hilo-pill-principal">
                                                        PRINCIPAL
                                                    </span>
                                                )}
                                            </span>
                                            <span
                                                className="hilo-estado"
                                                style={{ color: getEstadoColor(item.estado) }}
                                            >
                                                {ESTADO_LABELS[item.estado] || item.estado}
                                            </span>
                                        </div>
                                        <div className="hilo-meta">
                                            {item.creador_nombre || 'Usuario eliminado'}
                                            {' · '}
                                            {formatDateTimeShort(item.created_at)}
                                            {' · '}
                                            <span className="hilo-total">{formatCurrency(item.total, item.moneda)}</span>
                                        </div>
                                        <div className="hilo-actions">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={<Eye size={13} />}
                                                onClick={() => navegarA(item, 'detalle')}
                                            >
                                                Ver detalle
                                            </Button>
                                            {esBorrador && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    leftIcon={<Pencil size={13} />}
                                                    onClick={() => navegarA(item, 'editar')}
                                                >
                                                    Editar
                                                </Button>
                                            )}
                                            {!esPrincipal && (
                                                <Tooltip content={hiloCerrado
                                                    ? `No se puede cambiar: la cotización ya fue ${principalActual?.estado}`
                                                    : 'Marcar esta versión como la visible en el listado'}>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        leftIcon={<Star size={13} />}
                                                        onClick={() => handleMarcarPrincipal(item)}
                                                        disabled={marcando === item.id || hiloCerrado}
                                                    >
                                                        {marcando === item.id ? 'Aplicando…' : 'Volver principal'}
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            )}
        </Modal>
    );
};

export default HiloVersionesModal;
